import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { LocalAiLeadBudgetRequest } from "@/api/local-ai-lead-api"

import { Inbox } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

import { LeadDenyDialog } from "./lead-deny-dialog"
import { LeadRequestsTable } from "./lead-requests-table"
import {
  formatLeadSek,
  leadApprovableBlocks,
  leadRequestDisplayName,
  leadRequestErrorKey,
  readLeadRequestError,
} from "../lead-requests-utils"
import {
  useApproveLocalAiLeadRequestMutation,
  useDenyLocalAiLeadRequestMutation,
  useLocalAiLeadRequestsQuery,
} from "../queries"

/**
 * "Requests waiting" — the local AI lead's in-app approval queue (spec R13).
 *
 * It appears only when the platform has actually delegated something: a
 * `lead_allowance_sek_per_month` of 0 (the shipped default) means leads
 * approve nothing, so the card would be a permanently empty promise. A tenant
 * with fair use switched off answers 503 `fair_use_not_configured` and gets
 * the same treatment — no card, no error, because nothing is wrong.
 *
 * Every rule behind the two buttons is the backend's: assignment, no
 * self-approval, the month lock and the allowance ceiling. The card's job is
 * to state the allowance up front so a lead is not told "no" by a 409 after
 * pressing Approve, and to translate the refusal codes when one arrives
 * anyway (another lead got there first; an admin resolved it; the month was
 * locked while the page was open).
 */
export function LeadRequestsCard() {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? i18n.language
  const [denyTarget, setDenyTarget] = useState<LocalAiLeadBudgetRequest | null>(null)

  const requestsQuery = useLocalAiLeadRequestsQuery("pending")
  const approveMutation = useApproveLocalAiLeadRequestMutation()
  const denyMutation = useDenyLocalAiLeadRequestMutation()

  const data = requestsQuery.data
  const allowance = data?.allowance
  const rows = data?.requests ?? []
  const approvableBlocks = leadApprovableBlocks(allowance)
  const isResolving = approveMutation.isPending || denyMutation.isPending

  const notifyFailure = (error: unknown) => {
    const parsed = readLeadRequestError(error)
    const key = leadRequestErrorKey(parsed.code)
    if (key) {
      toast.error(
        t(key, {
          allowance: formatLeadSek(parsed.allowanceSek, locale),
          used: formatLeadSek(parsed.usedSek, locale),
          requested: formatLeadSek(parsed.requestedSek, locale),
          defaultValue: parsed.message ?? "",
        })
      )
      return
    }
    toast.error(
      parsed.message ??
        t("localAiLead.requests.genericError", {
          defaultValue: "The request could not be resolved. Please try again.",
        })
    )
  }

  const handleApprove = (row: LocalAiLeadBudgetRequest) => {
    approveMutation.mutate(
      { requestId: row.id, blocks: 1, month: row.year_month },
      {
        onSuccess: () =>
          toast.success(
            t("localAiLead.requests.approved", {
              user: leadRequestDisplayName(row),
              defaultValue: "Approved extra budget for {{user}}",
            })
          ),
        onError: notifyFailure,
      }
    )
  }

  const handleDeny = (reason: string) => {
    const row = denyTarget
    if (!row) return
    denyMutation.mutate(
      { requestId: row.id, reason, month: row.year_month },
      {
        onSuccess: () => {
          setDenyTarget(null)
          toast.success(
            t("localAiLead.requests.denied", {
              user: leadRequestDisplayName(row),
              defaultValue: "Turned down {{user}}'s request",
            })
          )
        },
        onError: notifyFailure,
      }
    )
  }

  if (requestsQuery.isLoading) {
    return <Skeleton className="h-40 rounded-lg" />
  }

  // Not delegated, fair use off, or the endpoint is unavailable: this card has
  // nothing to say, and a lead is not the person who can fix any of the three.
  if (requestsQuery.isError || !allowance || allowance.allowance_sek <= 0) {
    return null
  }

  return (
    <Card className="rounded-lg" data-testid="local-ai-lead-requests-card">
      <CardHeader className="gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>
            {t("localAiLead.requests.title", { defaultValue: "Requests waiting" })}
          </CardTitle>
          {rows.length > 0 && (
            <Badge data-testid="local-ai-lead-requests-count" variant="warning">
              {rows.length}
            </Badge>
          )}
        </div>
        <CardDescription data-testid="local-ai-lead-allowance">
          {t("localAiLead.requests.allowance", {
            remaining: formatLeadSek(allowance.remaining_sek, locale),
            allowance: formatLeadSek(allowance.allowance_sek, locale),
            month: allowance.year_month,
            defaultValue: "{{remaining}} of {{allowance}} SEK left to approve in {{month}}.",
          })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div
            className="text-muted-foreground flex items-center gap-2 py-6 text-sm"
            data-testid="local-ai-lead-requests-empty"
          >
            <Inbox aria-hidden="true" className="size-4 shrink-0" />
            {t("localAiLead.requests.empty", {
              defaultValue: "No one is waiting for an answer right now.",
            })}
          </div>
        ) : (
          <LeadRequestsTable
            approvableBlocks={approvableBlocks}
            blockCapSek={allowance.block_cap_sek}
            isResolving={isResolving}
            rows={rows}
            onApprove={handleApprove}
            onDeny={setDenyTarget}
          />
        )}
      </CardContent>
      <LeadDenyDialog
        isPending={denyMutation.isPending}
        motivation={denyTarget?.motivation}
        open={denyTarget !== null}
        userLabel={denyTarget ? leadRequestDisplayName(denyTarget) : ""}
        onSubmit={handleDeny}
        onOpenChange={(open) => {
          if (!open) setDenyTarget(null)
        }}
      />
    </Card>
  )
}
